import { getAccessToken, isTokenExpired, refreshSession } from '../api/token';

/**
 * fetch с авторизацией и самовосстановлением сессии — аналог логики
 * интерцепторов axios (src/api/axios.js) для «сырых» запросов (аудио и т.п.):
 * - если access-токен отсутствует или истёк — сначала пробуем refreshSession();
 * - при ответе 401 — обновляем сессию и повторяем запрос один раз;
 * - при ответе 429 — ждём секунду и повторяем один раз.
 *
 * @param {string} url - Абсолютный URL запроса
 * @param {Object} [options] - Опции fetch (method, headers и т.д.)
 * @returns {Promise<Response>} Ответ fetch (может быть неуспешным — проверяйте response.ok)
 */
export const fetchWithAuth = async (url, options = {}) => {
  const doFetch = (token) =>
    fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

  let token = getAccessToken();

  // Токена нет или он истёк — пробуем обновить сессию до запроса
  if (!token || isTokenExpired(token)) {
    try {
      token = await refreshSession();
    } catch {
      // refresh не удался — шлём как есть, получим 401 и обработаем ниже
      token = getAccessToken();
    }
  }

  let response = await doFetch(token);

  // 401 — обновляем сессию и повторяем один раз
  if (response.status === 401) {
    try {
      const newToken = await refreshSession();
      response = await doFetch(newToken);
    } catch {
      // refresh не удался — возвращаем исходный 401
    }
  }

  // 429 — лимит запросов, ждём и повторяем один раз
  if (response.status === 429) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    response = await doFetch(getAccessToken());
  }

  return response;
};
